const ONE_DAY_TO_MILLISECONDS = 24 * 60 * 60 * 1000;

/**
 * 日時情報のフォーマッタ
 */
class Formatter {
  _d = null;
  _defaults = "";
  constructor(date, defaults = "-") {
    this._defaults = defaults;
    if (date == null) {
      this._d = null;
      return;
    }

    // ゼロ埋めした数値を返す
    const padding = (number, digit) => {
      if (`${number}`.length >= digit) {
        return `${number}`;
      }
      return `0000${number}`.slice(-digit);
    };

    // 日時情報をフォーマットした年月日それぞれにパースする
    this._d = {
      date: {
        y: date.getFullYear(),
        m: date.getMonth() + 1,
        d: date.getDate(),
        y04: padding(date.getFullYear(), 4),
        m02: padding(date.getMonth() + 1, 2),
        d02: padding(date.getDate(), 2),
      },
      time: {
        h: date.getHours(),
        m: date.getMinutes(),
        s: date.getSeconds(),
        h02: padding(date.getHours(), 2),
        m02: padding(date.getMinutes(), 2),
        s02: padding(date.getSeconds(), 2),
      },
      raw: date,
    };
  }

  /**
   * 日本語の年月日を返す
   */
  get localizedDate() {
    if (this._d == null) {
      return this._defaults;
    }
    const d = this._d;
    return `${d.date.y}年 ${d.date.m}月 ${d.date.d}日`;
  }

  /**
 * 日本語の年月を返す
 */
  get localizedMonth() {
    if (this._d == null) {
      return this._defaults;
    }
    const d = this._d;
    return `${d.date.y}年 ${d.date.m}月`;
  }

  /**
 * 日本語の年月を返す(3か月表示)
 */
  get localized3Month() {
    if (this._d == null) {
      return this._defaults;
    }
    const d = this._d;
    return `${d.date.y}年 ${d.date.m-1}月～${d.date.m+1}月`;
  }

  /**
   * yyyy-mm-dd形式の年月日を返す
   */
  get systemDate() {
    if (this._d == null) {
      return this._defaults;
    }
    const d = this._d;
    return `${d.date.y}-${d.date.m02}-${d.date.d02}`;
  }

  /**
   * データを任意の形式の文字列に変換する
   * @param {フォーマッタのデータを引数にする関数} formatter_function
   * @returns 日時文字列
   */
  build(formatter_function) {
    if (this._d == null) {
      return this._defaults;
    }
    return formatter_function(this._d);
  }
}

/**
 * 日時データ、日時文字列のラッパ
 */
class DateWrapper {
  _date = null;

  /**
   * 日時、日時文字列、タイムスタンプ、DateWrapperのいずれかをラップする
   * @param {ラップするデータ} date_value
   */
  constructor(date_value) {
    // 初期値はnullとする
    this._date = null;
    // 無効な数値、nullでなければパースをする
    if (date_value) {
      if (date_value instanceof Date) {
        // 日時オブジェクトから読み込む
        this._date = date_value;
      } else if (date_value instanceof DateWrapper) {
        // 他のDateWrapperから読み込む
        this._date = date_value._date;
      } else if (typeof date_value == "string") {
        // 文字列から読み込む
        // 不正な形式であればnullとする
        const millisec = Date.parse(date_value);
        if (!Number.isNaN(millisec)) {
          this._date = new Date(millisec);
        }
      } else {
        // ミリ秒から読み込む
        this._date = new Date(date_value);
      }
    }
  }

  /**
   * 日時文字列に変換するフォーマッタを返す
   */
  get format() {
    return new Formatter(this._date);
  }

  /**
   * ミリ秒のタイムスタンプを返す
   */
  get timestamp() {
    if (this._date == null) {
      return -1;
    }
    return this._date.getTime();
  }

  /**
   * 無効な日付ならfalseを返す
   */
  get isValid() {
    if (this._date == null) {
      return false;
    }
    return true;
  }

  /**
   * DateWrapper、または日時、日時文字列と比較する
   * @param {比較するデータ} date_value
   * @returns 比較対象が古い日時であれば1以上、比較対象が新しい日時であれば-1以下を返す
   */
  compare(date_value) {
    const a = this.timestamp;
    let b = 0;
    if (date_value instanceof Date) {
      b = date_value.getTime();
    } else if (date_value instanceof DateWrapper) {
      b = date_value.timestamp;
    } else if (typeof date_value == "string") {
      b = Date.parse(date_value);
    } else {
      b = date_value;
    }
    if (Number.isNaN(b)) {
      b = -1;
    }
    return a - b;
  }

  /**
   * 格納している日時の時間を進める
   * @param {ミリ秒単位のタイムスタンプ} timestamp
   * @returns 変更した時間
   */
  append(timestamp) {
    return new DateWrapper(this.timestamp + timestamp);
  }

  /**
   * 格納している日時をn日進める
   * @param {日数} days
   * @returns 変更した時間
   */
  appendDay(days) {
    return this.append(days * ONE_DAY_TO_MILLISECONDS);
  }
}

export default DateWrapper;
